import { useMemo } from 'react'
import { CircularProgress, Stack, Switch, Typography } from '@mui/material'
import { useGetProjectFeatures } from '@/service-library/hooks/project-features'
import {
  useCreateProjectProjectFeature,
  useDeleteProjectProjectFeature,
  useGetProjectProjectFeatures,
} from '@/service-library/hooks/project-project-features'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import SettingsPage from '@/components/shared/settings/SettingsPage'
import SettingsRow from '@/components/shared/settings/SettingsRow'
import SettingsSection from '@/components/shared/settings/SettingsSection'

export default function ProjectFeaturesPage() {
  const { project } = useProjectContext()

  const { projectFeatures, isPending: isPendingProjectFeatures } =
    useGetProjectFeatures({
      filters: { limit: '1000' },
    })

  const {
    projectProjectFeatures,
    isPending: isPendingProjectProjectFeatures,
    queryKey,
  } = useGetProjectProjectFeatures({
    filters: { project_id: project.id, limit: '1000' },
  })

  const { createProjectProjectFeature } = useCreateProjectProjectFeature({
    listQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Failed to enable feature. Please try again later.')
    },
  })

  const { deleteProjectProjectFeature } = useDeleteProjectProjectFeature({
    listQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Failed to disable feature. Please try again later.')
    },
  })

  const projectProjectFeatureIdByFeatureId = useMemo(
    () =>
      new Map(
        projectProjectFeatures.map((projectProjectFeature) => [
          projectProjectFeature.project_feature_id,
          projectProjectFeature.id,
        ]),
      ),
    [projectProjectFeatures],
  )

  const isPending = isPendingProjectFeatures || isPendingProjectProjectFeatures

  function handleToggle(projectFeatureId: string, checked: boolean) {
    const projectProjectFeatureId =
      projectProjectFeatureIdByFeatureId.get(projectFeatureId)
    if (checked) {
      if (!projectProjectFeatureId) {
        createProjectProjectFeature({
          id: generateUuid(),
          project_id: project.id,
          project_feature_id: projectFeatureId,
        })
      }
    } else {
      if (projectProjectFeatureId) {
        deleteProjectProjectFeature(projectProjectFeatureId)
      }
    }
  }

  return (
    <>
      <ProjectBreadcrumb label="Features" url="../features" />

      <SettingsPage
        title="Features"
        width="sm"
        description="Turn optional features on or off for this project. Changes save right away."
      >
        {isPending ? (
          <Stack alignItems="center" sx={{ p: 4 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <SettingsSection>
            {projectFeatures.length === 0 ? (
              <SettingsRow>
                <Typography variant="body2" color="text.secondary">
                  No features available.
                </Typography>
              </SettingsRow>
            ) : (
              projectFeatures.map((feature) => {
                const switchId = `project-feature-${feature.id}`
                return (
                  <SettingsRow
                    key={feature.id}
                    label={feature.name}
                    description={feature.description}
                    htmlFor={switchId}
                  >
                    <Switch
                      id={switchId}
                      checked={projectProjectFeatureIdByFeatureId.has(
                        feature.id,
                      )}
                      onChange={(_e, checked) => {
                        handleToggle(feature.id, checked)
                      }}
                    />
                  </SettingsRow>
                )
              })
            )}
          </SettingsSection>
        )}
      </SettingsPage>
    </>
  )
}
